import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { TrendingUp, Users, Zap, BarChart3, Target, CheckCircle, ArrowRight, ExternalLink, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const businessImpacts = [
    {
      title: "Engagement",
      description: "Recognition is one of the fastest ways to boost engagement.",
      icon: Zap,
      link: { text: "how recognition impacts engagement at work", url: "/blog/how-employee-recognition-impacts-employee-engagement-at-work" }
    },
    {
      title: "Retention",
      description: "Appreciated employees are far more likely to stay.",
      icon: Users,
      link: { text: "recognition as a tool for reducing turnover", url: "/blog/recognition-as-a-tool-for-reducing-employee-turnover" }
    },
    {
      title: "Productivity & Quality",
      description: "Specific, timely recognition reinforces high-impact behaviors.",
      icon: BarChart3,
      link: { text: "boosting morale and productivity", url: "/blog/boosting-employee-morale-and-productivity-in-the-workplace" }
    },
    {
      title: "Culture & Belonging",
      description: "When recognition is tied to values, it makes culture visible and shared.",
      icon: Target,
      link: { text: "why recognition is a strategic investment", url: "/blog/why-recognition-is-a-strategic-investment" }
    }
  ];

  const employeeJourney = [
    {
      stage: "Onboarding",
      description: "Early shoutouts make new hires feel welcome and confident."
    },
    {
      stage: "Development", 
      description: "Recognizing progress (not just outcomes) builds momentum and encourages learning."
    },
    {
      stage: "Milestones",
      description: "Anniversaries, promotions, and certifications are opportunities to show long-term appreciation.",
      link: { text: "the significance of milestones", url: "/blog/employee-milestones-and-the-significance-of-long-term-recognition" }
    },
    {
      stage: "Daily Work",
      description: "Peer-to-peer shoutouts keep recognition flowing between reviews.",
      link: { text: "getting started with peer recognition", url: "/blog/your-complete-guide-to-getting-started-with-peer-recognition" }
    }
  ];

  const strategicAdvantages = [
    {
      title: "Frictionless",
      description: "Recognition flows best when it lives in existing tools.",
      link: { text: "/integrations", url: "/integrations", context: "for Slack, Microsoft Teams, and more." }
    },
    {
      title: "Measurable", 
      description: "Impact needs to be tracked.",
      link: { text: "recognition analytics best practices", url: "/blog/how-to-leverage-employee-recognition-analytics-for-enhanced-engagement", context: "and survey questions to ask." }
    },
    {
      title: "Values-Aligned",
      description: "Tie recognition to values so it reinforces the culture you want to scale."
    },
    {
      title: "Inclusive",
      description: "Ensure both desk and frontline employees have access to recognition, whether mobile-first or offline."
    }
  ];

  const assemblyFeatures = [
    {
      title: "In-the-moment appreciation",
      description: "with peer-to-peer recognition that feels natural in daily workflows",
      link: { text: "peer-to-peer recognition", url: "/employee-recognition/peer-to-peer" }
    },
    {
      title: "Never-miss moments",
      description: "through automated milestone celebrations",
      link: { text: "milestone celebrations", url: "/employee-recognition/milestones" }
    },  
    {
      title: "Data-backed insights",
      description: "with dashboards and surveys to prove ROI and continuously improve programs"
    }
  ];

  return (
    <RecognitionGuideLayout
      title="Why is Employee Recognition Important?"
      description="Discover the proven business benefits of employee recognition programs and how recognition drives engagement, retention, productivity, and competitive advantage."
      currentStep={2}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <TrendingUp className="w-4 h-4" />
            Step 2 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Why is Employee Recognition Important?
          </h1>
          
          <div className="space-y-4">
            <p className="text-lg text-foreground leading-relaxed max-w-4xl">
              Employee recognition isn't just about being nice-it's one of the most effective ways to strengthen performance and culture at the same time. When employees feel valued, they're more engaged, more loyal, and more willing to put in discretionary effort. When recognition is missing, the opposite is true: people disengage, morale erodes, and turnover rises.
            </p>
            
            <p className="text-muted-foreground leading-relaxed max-w-4xl">
              Recognition matters because it connects everyday contributions to the bigger picture. It reinforces the behaviors that drive results, signals what your company values most, and builds a sense of belonging. Independent research backs this up-{" "}
              <a 
                href="https://www.gallup.com/workplace/236441/employee-recognition-low-cost-high-impact.aspx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1"
              >
                <strong className="text-foreground">Gallup</strong>
                <ExternalLink className="w-3 h-3" />
              </a>{" "}
              found that employees who receive regular recognition are more productive, more engaged, and less likely to leave. And{" "}
              <a 
                href="https://www.shrm.org/topics-tools/flagships/all-things-work/employee-recognition-can-go-long-way-but-has-to-be-done-right" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1"
              >
                <strong className="text-foreground">SHRM</strong>
                <ExternalLink className="w-3 h-3" />
              </a>{" "}
              reports that lack of appreciation is one of the most common reasons employees cite for quitting.
            </p>
            
            <p className="text-muted-foreground leading-relaxed max-w-4xl">
              If you're building the case internally, start with this overview on{" "}
              <a href="/blog/the-importance-of-a-culture-of-recognition-in-the-workplace" className="text-primary hover:text-primary/80 underline">
                the importance of a recognition culture
              </a>.
            </p>
          </div>
        </div>

        {/* The Business Case for Recognition */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              The Business Case for Recognition
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Recognition directly impacts the outcomes leaders care about most
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {businessImpacts.map((impact, index) => {
              const IconComponent = impact.icon;
              return (
                <Card key={index} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center border border-primary/20">
                        <IconComponent className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground">{impact.title}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {impact.description} Learn more in{" "}
                      <a href={impact.link.url} className="text-primary hover:text-primary/80 underline">
                        {impact.link.text}
                      </a>.
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Recognition Across the Employee Experience */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Recognition Across the Employee Experience
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Recognition shouldn't be an annual event-it should be woven into the entire employee journey
            </p>
          </div>
          
          <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {employeeJourney.map((stage, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div>
                      <div className="font-medium text-foreground mb-1">{stage.stage}</div>
                      <div className="text-sm text-muted-foreground">
                        {stage.description}
                        {stage.link && (
                          <span>
                            {" "}See{" "}
                            <a href={stage.link.url} className="text-primary hover:text-primary/80 underline">
                              {stage.link.text}
                            </a>.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-accent/10 p-6 rounded-lg border border-accent/20">
                <p className="text-muted-foreground leading-relaxed">
                  For distributed and hybrid teams, recognition is especially powerful-it bridges the "visibility gap" when employees aren't physically together. Managers can find practical advice in{" "}
                  <a href="/blog/how-to-manage-engage-employees-in-a-hybrid-workplace" className="text-primary hover:text-primary/80 underline">
                    how to engage hybrid employees
                  </a>.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Recognition as a Strategic Advantage */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Recognition as a Strategic Advantage
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Organizations that embed recognition into their culture consistently outperform those that don't
            </p>
          </div>
          
          <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="grid gap-6">
                {strategicAdvantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-bold shrink-0 border border-primary/20">
                      {index + 1}
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium text-foreground">{advantage.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {advantage.description}
                        {advantage.link && (
                          <span>
                            {" "}Explore{" "}
                            <a href={advantage.link.url} className="text-primary hover:text-primary/80 underline">
                              {advantage.link.text}
                            </a>
                            {advantage.link.context && <span> {advantage.link.context}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* How Assembly Helps */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
            <CardContent className="p-8 text-center space-y-6">
              <div className="space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                
                <h3 className="text-2xl font-bold text-foreground">
                  How Assembly Helps
                </h3>
                
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Assembly is built to make recognition simple, frequent, and impactful
                </p>
              </div>
              
              <div className="grid gap-4 text-left max-w-4xl mx-auto">
                {assemblyFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <div>
                      <span className="font-medium text-foreground">{feature.title}</span>
                      <span className="text-muted-foreground"> {feature.description}</span>
                      {feature.link && (
                        <span>
                          .{" "}
                          <a href={feature.link.url} className="text-primary hover:text-primary/80 underline">
                            {feature.link.text}
                          </a>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-primary/10 p-6 rounded-lg border border-primary/20 max-w-2xl mx-auto">
                <p className="text-muted-foreground">
                  When recognition becomes a habit instead of a task, employees feel valued-and businesses thrive.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <h2 className="text-2xl font-bold text-foreground text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  What are the main business benefits of employee recognition?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Employee recognition drives four key business benefits: increased engagement (31% higher according to Gallup), improved retention (reduces turnover by up to 45%), higher productivity (21% increase), and stronger culture and belonging. These benefits directly impact your bottom line.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  How does recognition impact employee retention?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  79% of employees who leave their jobs cite lack of appreciation as a key reason. Recognition programs can reduce voluntary turnover by 31-45% by making employees feel valued and appreciated for their contributions. This saves significant replacement costs.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Does recognition work for remote and hybrid teams?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Yes, recognition is especially powerful for distributed teams as it bridges the "visibility gap" when employees aren't physically together. Digital recognition platforms help create connection and visibility across locations, time zones, and work arrangements.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  How quickly can organizations see results from recognition programs?
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Most organizations see initial improvements in engagement and morale within 30-60 days. Measurable business impacts like reduced turnover and increased productivity typically emerge within 3-6 months of consistent program implementation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps CTA */}
        <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
          <CardContent className="p-8 text-center space-y-6">
            <h3 className="text-2xl font-bold text-foreground">
              Ready to Explore Recognition Types?
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Now that you understand why recognition matters, discover the different types of recognition and how to choose the right approach for your team.
            </p>
            <Button size="lg" asChild>
              <a href="/employee-recognition-program-guide-assembly/types-of-employee-recognition-and-rewards-assembly" className="flex items-center gap-2">
                Explore Recognition Types
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </RecognitionGuideLayout>
  );
}